/**
 * All UI copy, in both languages.
 *
 * Quiet, archival, second person — never marketplace language, in either
 * script. `en` is typed against `fa`, so a forgotten key is a build error
 * rather than a blank label on someone's phone.
 */
import { getLang, useLang, type Lang } from "./lang";
import type { AccessLevel, RightsLicense } from "../api/types";

/**
 * Declared out here rather than inline so `Strings = typeof fa` carries the
 * `Record<...>` annotation: a licence code added to the API is then a build
 * error in both languages, not a blank row in a picker.
 *
 * Creative Commons names stay Latin in both scripts — they are proper names,
 * like the ISO code on a currency term. Only the gloss changes.
 */
const faLicenses: Record<RightsLicense, string> = {
  all_rights_reserved: "تمام حقوق محفوظ است",
  cc_by: "CC BY 4.0 — انتساب",
  cc_by_sa: "CC BY-SA 4.0 — انتساب / همانند",
  cc_by_nd: "CC BY-ND 4.0 — انتساب / بدون تغییر",
  cc_by_nc: "CC BY-NC 4.0 — انتساب / غیرتجاری",
  cc_by_nc_sa: "CC BY-NC-SA 4.0 — غیرتجاری / همانند",
  cc_by_nc_nd: "CC BY-NC-ND 4.0 — غیرتجاری / بدون تغییر",
  cc0: "CC0 1.0 — واگذاری به مالکیت عمومی",
  public_domain: "مالکیت عمومی",
  custom: "شرایط سفارشی",
};

const faAccess: Record<AccessLevel, string> = {
  private: "خصوصی",
  unlisted: "فقط با پیوند",
  public: "عمومی",
};

const enLicenses: Record<RightsLicense, string> = {
  all_rights_reserved: "All rights reserved",
  cc_by: "CC BY 4.0",
  cc_by_sa: "CC BY-SA 4.0",
  cc_by_nd: "CC BY-ND 4.0",
  cc_by_nc: "CC BY-NC 4.0",
  cc_by_nc_sa: "CC BY-NC-SA 4.0",
  cc_by_nc_nd: "CC BY-NC-ND 4.0",
  cc0: "CC0 1.0",
  public_domain: "Public domain",
  custom: "Custom terms",
};

const enAccess: Record<AccessLevel, string> = {
  private: "Private",
  unlisted: "Unlisted link",
  public: "Public",
};

const fa = {
  appName: "آرشیو آثار",

  tabs: {
    works: "آثار",
    browse: "دسته‌بندی",
    add: "افزودن",
  },

  actions: {
    save: "ذخیره",
    saving: "در حال ذخیره…",
    saved: "ذخیره شد",
    cancel: "انصراف",
    edit: "ویرایش",
    delete: "حذف",
    confirmDelete: "حذف اثر",
    undo: "بازگردانی",
    clearAll: "پاک کردن همه",
    apply: "اعمال",
    addOption: "افزودن گزینه",
    add: "افزودن",
    retry: "تلاش دوباره",
    close: "بستن",
    search: "جستجو",
    filter: "فیلتر",
    select: "انتخاب",
    remove: "حذف",
    makePrimary: "تصویر اصلی شود",
    choose: "انتخاب فایل",
    open: "باز کردن",
    back: "بازگشت",
    done: "تمام",
    viewGrid: "نمایش شبکه‌ای",
    viewList: "نمایش فهرستی",
    language: "زبان",
  },

  home: {
    title: "آثار",
    searchPlaceholder: "جستجو در آثار",
    emptyTitle: "هنوز اثری نیست",
    emptyBody: "اولین اثر خود را ثبت کنید.",
    noHitsTitle: "نتیجه‌ای نیست",
    noHitsBody: "جستجو یا فیلترها را تغییر دهید.",
    countSuffix: (_n: number) => "اثر",
  },

  browse: {
    title: "دسته‌بندی",
    lead: "آثار بر اساس نوع",
    emptyTitle: "هنوز دسته‌ای پر نشده",
    emptyBody: "با ثبت اولین اثر، دسته‌ها اینجا شکل می‌گیرند.",
    allSubtypes: "همه",
    any: "همه",
    subtypesLabel: "زیرنوع",
    pickTypeFirst: "ابتدا نوع اثر",
    /** Persian does not inflect the counted noun; English does. */
    countSuffix: (_n: number) => "اثر",
  },

  filters: {
    title: "فیلترها",
    yearFrom: "از سال",
    yearTo: "تا سال",
    unknownYear: "سال نامشخص",
    anyOf: "هر کدام از موارد",
    activeCount: (n: number) => `${n} فیلتر فعال`,
    hasMedia: "دارای تصویر",
    removeOne: (label: string) => `حذف فیلتر ${label}`,
    yearRange: (from: string, to: string) => `${from} تا ${to}`,
    yearSince: (from: string) => `از ${from}`,
    yearUntil: (to: string) => `تا ${to}`,
  },

  form: {
    newTitle: "اثر تازه",
    editTitle: "ویرایش اثر",
    sections: {
      identity: "شناسایی",
      classification: "طبقه‌بندی",
      year: "سال خلق",
      dimensions: "ابعاد",
      material: "مدیوم و سبک",
      ownership: "مالکیت و ارزش",
      condition: "وضعیت اثر",
      provenance: "منشأ",
      authenticity: "اصالت",
      media: "رسانه",
    },
    titlePlaceholder: "عنوان اثر",
    inventoryPlaceholder: "اختیاری",
    artistPlaceholder: "انتخاب یا افزودن هنرمند",
    artistSearchPlaceholder: "نام هنرمند",
    artistCreate: (name: string) => `افزودن «${name}» به فهرست هنرمندان`,
    noArtists: "هنوز هنرمندی ثبت نشده",
    unit: "سانتی‌متر",
    yearPrecision: {
      exact: "دقیق",
      circa: "حدوداً",
      range: "بازه",
      unknown: "نامشخص",
    },
    optional: "اختیاری",
    none: "—",
    newOptionPlaceholder: "عنوان گزینهٔ تازه",
    dirtyDiscard: "تغییرات ذخیره‌نشده رها شود؟",
    requiredType: "نوع اثر را انتخاب کنید.",
    requiredTitle: "عنوان اثر را وارد کنید.",
    currencyRequired: "برای ثبت مبلغ، واحد پول را انتخاب کنید.",
    dimensionPositive: "ابعاد باید بزرگ‌تر از صفر باشد.",
    yearStartRequired: "سال را وارد کنید.",
    yearEndRequired: "پایان بازه را وارد کنید.",
    yearOrder: "پایان بازه نمی‌تواند پیش از آغاز آن باشد.",
    subtypeMismatch: "این زیرنوع به نوع انتخاب‌شده تعلق ندارد.",
    rightsNoteRequired: "برای شرایط سفارشی، شرح حقوق را وارد کنید.",
    partsEmpty: "برای اثر چندپارچه، هر پانل را جداگانه ثبت کنید.",
    partLine: (n: number) => `جزء ${n}`,
    partLabelPlaceholder: "مثلاً پانل چپ",
    addPart: "افزودن جزء",
    editionBoth: "شمارهٔ نسخه و تیراژ را با هم وارد کنید، یا هر دو را خالی بگذارید.",
    editionMin: "نسخه و تیراژ باید حداقل ۱ باشند.",
    editionOrder: "شمارهٔ نسخه نمی‌تواند بزرگ‌تر از تیراژ باشد.",
  },

  share: {
    title: "صفحهٔ عمومی",
    toggle: "نمایش این اثر با کد QR",
    intro:
      "بازدیدکننده با اسکن کد، عنوان، تصویر، ابعاد، مدیوم و سال را می‌بیند. یادداشت‌ها، قیمت، منشأ، مدارک و کد انبار خصوصی می‌مانند.",
    offNote: "خاموش است؛ کسی که کد را اسکن کند چیزی نمی‌بیند.",
    printHint: "این کد را روی برچسب اثر چاپ کنید.",
    copy: "کپی",
    copied: "پیوند کپی شد",
    copyFailed: "کپی نشد؛ پیوند را دستی کپی کنید.",
    send: "ارسال",
    open: "باز کردن",
    rotate: "کد جدید",
    rotateTitle: "ساخت کد جدید",
    rotatePrompt: "کد فعلی باطل می‌شود و برچسب‌های چاپ‌شده با آن دیگر کار نمی‌کنند.",
    rotated: "کد جدید ساخته شد",
    published: "صفحهٔ عمومی روشن شد",
    unpublished: "صفحهٔ عمومی خاموش شد",
    qrLabel: "کد QR صفحهٔ عمومی",
    download: "دریافت فایل QR",
    downloading: "در حال ارسال…",
    downloadHint: "فایل چاپی QR در گفتگو با ربات فرستاده می‌شود.",
    sent: "فایل QR در گفتگو با ربات فرستاده شد",
    startBot: "اول گفتگو با ربات را باز کنید و Start را بزنید، بعد دوباره امتحان کنید.",
    sendFailed: "ارسال نشد؛ اتصال به تلگرام برقرار نشد. دوباره امتحان کنید.",
  },

  catalog: {
    settingsTitle: "صفحه و هم‌ویرایشگران",
    switchLabel: "انتخاب آرشیو",
    ownCatalog: "آرشیو من",
    sharedCatalog: "آرشیو مشترک",
    publicCatalogTitle: "فهرست عمومی",
    catalogPublicToggle: "فهرست عمومی من در این نامک دیده شود",
    profileSaved: "تنظیمات ذخیره شد",
    membersTitle: "هم‌ویرایشگران",
    noMembers: "هنوز کسی دعوت نشده.",
    anonymousMember: "بدون نام",
    memberRemoved: "هم‌ویرایشگر حذف شد",
    removeCoeditTitle: "حذف هم‌ویرایشگر؟",
    removeCoeditBody: "دیگر این آرشیو را نمی‌بینند. آرشیو خودشان دست‌نخورده می‌ماند.",
    removeCoeditConfirm: "حذف دسترسی",
    yourTelegramId: "شناسه تلگرام شما (برای TELEGRAM_ALLOWED_USER_IDS)",
    inviteTitle: "دعوت به هم‌ویرایش این آرشیو",
    inviteHint:
      "روی آرشیو شما کار می‌کنند — نه کاتالوگ جدا. پیوند را باز می‌کنند، Start، بعد Open App.",
    inviteCreate: "ساخت پیوند هم‌ویرایش",
    inviteCreating: "در حال ساخت…",
    inviteCreated: "پیوند هم‌ویرایش ساخته شد",
    inviteLinkLabel: "پیوند هم‌ویرایش",
    inviteShareText: "دعوت به ویرایش آرشیو آثار",
    theme: {
      default: "پیش‌فرض",
      tina: "Tina",
    },
  },

  access: {
    title: "دسترسی به اپ",
    hint: "فقط با پیوند دعوت. هر نفر آرشیو خودش را می‌گیرد؛ هم‌ویرایش آرشیو شما جداست.",
    noMembers: "هنوز کسی در فهرست دسترسی نیست.",
    anonymousMember: "بدون نام",
    roleAdmin: "ادمین",
    roleUser: "کاربر",
    memberRemoved: "دسترسی به اپ برداشته شد",
    removeTitle: "حذف دسترسی به اپ؟",
    removeBody:
      "دیگر نمی‌توانند مینی‌اپ را باز کنند. آرشیو و آثارشان پاک نمی‌شود؛ فقط مجوز ورود برداشته می‌شود.",
    removeConfirm: "حذف دسترسی",
    admitTitle: "دعوت کاربر",
    admitHint: "ورود به اپ با آرشیو شخصی. مدیریت دسترسی ندارد.",
    admitInviteCreate: "ساخت پیوند کاربر",
    admitInviteCreated: "پیوند کاربر ساخته شد",
    admitLinkLabel: "پیوند کاربر",
    adminTitle: "دعوت ادمین",
    adminHint: "ورود به اپ با آرشیو شخصی، به‌علاوه دعوت کاربر (و ادمین).",
    adminInviteCreate: "ساخت پیوند ادمین",
    adminInviteCreated: "پیوند ادمین ساخته شد",
    adminLinkLabel: "پیوند ادمین",
    admitShareText: "دعوت کاربر به Art Directory",
    adminShareText: "دعوت ادمین Art Directory",
  },

  publicPage: {
    notFound: "این اثر در دسترس نیست",
    notFoundBody: "پیوند باطل شده یا نمایش آن خاموش شده است.",
    catalogNotFound: "این فهرست در دسترس نیست",
    catalogNotFoundBody: "نامک اشتباه است یا فهرست عمومی خاموش شده.",
    retry: "تلاش دوباره",
    gallery: (n: number, total: number) => `تصویر ${n} از ${total}`,
    specs: "مشخصات",
    edition: (n: number, size: number) => `نسخه ${n} از ${size}`,
    lifeSize: "اندازهٔ طبیعی",
    uncategorized: "بدون دسته",
    filterAll: "همه",
    filterCategory: "دسته",
    filterArtist: "هنرمند",
    searchPlaceholder: "جستجو در فهرست…",
    noFilterHits: "چیزی با این فیلترها نیست",
    clearFilters: "پاک‌کردن فیلترها",
    navWorks: "آثار",
    navWork: "اثر",
    navBack: "بازگشت به فهرست",
    footer: "فهرست عمومی آرشیو",
  },

  detail: {
    untitled: "بدون عنوان",
    unknownArtist: "هنرمند نامشخص",
    noMedia: "بدون تصویر",
    deletePrompt: "این اثر از فهرست حذف می‌شود. می‌توانید بلافاصله بازگردانید.",
    deleted: "اثر حذف شد",
    restored: "اثر بازگردانده شد",
    /** Never a verdict. A document is evidence and nothing more. */
    authenticityNote:
      "این مدارک شواهد ثبت‌شده‌اند، نه تأیید اصالت. داوری نهایی بر عهدهٔ کارشناس است.",
    noFiles: "فایلی پیوست نشده",
    accessNote: "این اثر با کسی به اشتراک گذاشته نشده؛ سطح دسترسی فقط ثبت شده است.",
  },

  media: {
    primary: "تصویر اصلی",
    conditionFiles: "مستندات وضعیت",
    authenticityFiles: "مدارک / گواهی",
    pickImageOrVideo: "افزودن تصویر یا ویدیو",
    pickFile: "افزودن فایل",
    uploading: "در حال بارگذاری…",
    processing: "در حال آماده‌سازی…",
    uploadFailed: "بارگذاری ناموفق بود",
    captionPlaceholder: "توضیح (اختیاری)",
    tooLarge: (limitMb: number) => `حجم فایل بیش از ${limitMb} مگابایت است.`,
    tooMany: (max: number) => `برای هر اثر حداکثر ${max} تصویر یا ویدیو.`,
    quotaExceeded: "فضای ذخیره‌سازی شما پر شده است.",
    wrongKind: "این نوع فایل برای این بخش مجاز نیست.",
    afterSave: "پس از ذخیرهٔ اثر بارگذاری می‌شود.",
    rightsLicense: faLicenses,
    accessLevel: faAccess,
    rightsNotePlaceholder: "شرایط حقوقی این تصویر",
    /** v1 is owner-only. Nothing here is shared, and the copy must not imply it is. */
    accessNote:
      "این آرشیو فقط برای خودتان است. سطح دسترسی اکنون ثبت می‌شود تا در آیندهٔ اشتراک‌گذاری به کار بیاید.",
  },

  errors: {
    title: "مشکلی پیش آمد",
    network: "ارتباط با سرور برقرار نشد.",
    notInTelegram: "این صفحه را از داخل تلگرام باز کنید.",
    unauthorized: "هویت شما تأیید نشد. اپ را از داخل تلگرام دوباره باز کنید.",
    forbidden: "این حساب تلگرام اجازهٔ دسترسی ندارد.",
    notFound: "این اثر پیدا نشد.",
    throttled: (seconds: number) => `درخواست‌ها زیاد بود. ${seconds} ثانیه صبر کنید.`,
    referenced: "این گزینه هنوز در آثاری استفاده شده و حذف نمی‌شود.",
    server: "خطای سرور.",
    artistRequired: "نام هنرمند نمی‌تواند خالی باشد.",
    amountNegative: "مبلغ نمی‌تواند منفی باشد.",
    unknownYearWithValue: "برای سال نامشخص نباید عددی وارد شود.",
    typeInUse: "این نوع در آثاری استفاده شده؛ به‌جای حذف غیرفعالش کنید.",
    duplicateOption: "این گزینه از قبل وجود دارد.",
  },

  loading: "در حال بارگذاری…",
};

/** `en` is typed against this, so a forgotten key fails the build. */
export type Strings = typeof fa;

const en: Strings = {
  appName: "Art Directory",

  tabs: {
    works: "Works",
    browse: "Browse",
    add: "Add",
  },

  actions: {
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    cancel: "Cancel",
    edit: "Edit",
    delete: "Delete",
    confirmDelete: "Delete work",
    undo: "Undo",
    clearAll: "Clear all",
    apply: "Apply",
    addOption: "Add option",
    add: "Add",
    retry: "Try again",
    close: "Close",
    search: "Search",
    filter: "Filter",
    select: "Select",
    remove: "Remove",
    makePrimary: "Make primary image",
    choose: "Choose file",
    open: "Open",
    back: "Back",
    done: "Done",
    viewGrid: "Grid view",
    viewList: "List view",
    language: "Language",
  },

  home: {
    title: "Works",
    searchPlaceholder: "Search your works",
    emptyTitle: "No works yet",
    emptyBody: "Record your first work.",
    noHitsTitle: "No results",
    noHitsBody: "Change the search or the filters.",
    countSuffix: (n: number) => (n === 1 ? "work" : "works"),
  },

  browse: {
    title: "Browse",
    lead: "Works by type",
    emptyTitle: "No categories filled yet",
    emptyBody: "Categories take shape once you record your first work.",
    allSubtypes: "All",
    any: "Any",
    subtypesLabel: "Subtype",
    pickTypeFirst: "Choose a type first",
    countSuffix: (n: number) => (n === 1 ? "work" : "works"),
  },

  filters: {
    title: "Filters",
    yearFrom: "Year from",
    yearTo: "Year to",
    unknownYear: "Unknown year",
    anyOf: "Any of",
    activeCount: (n: number) => `${n} active ${n === 1 ? "filter" : "filters"}`,
    hasMedia: "Has an image",
    removeOne: (label: string) => `Remove filter ${label}`,
    yearRange: (from: string, to: string) => `${from} to ${to}`,
    yearSince: (from: string) => `From ${from}`,
    yearUntil: (to: string) => `Until ${to}`,
  },

  form: {
    newTitle: "New work",
    editTitle: "Edit work",
    sections: {
      identity: "Identity",
      classification: "Classification",
      year: "Creation year",
      dimensions: "Dimensions",
      material: "Medium and style",
      ownership: "Ownership and value",
      condition: "Condition",
      provenance: "Provenance",
      authenticity: "Authenticity",
      media: "Media",
    },
    titlePlaceholder: "Title of the work",
    inventoryPlaceholder: "Optional",
    artistPlaceholder: "Select or add an artist",
    artistSearchPlaceholder: "Artist name",
    artistCreate: (name: string) => `Add “${name}” to your artists`,
    noArtists: "No artists recorded yet",
    unit: "cm",
    yearPrecision: {
      exact: "Exact",
      circa: "Circa",
      range: "Range",
      unknown: "Unknown",
    },
    optional: "Optional",
    none: "—",
    newOptionPlaceholder: "Name of the new option",
    dirtyDiscard: "Discard unsaved changes?",
    requiredType: "Choose the artwork type.",
    requiredTitle: "Enter the title of the work.",
    currencyRequired: "Choose a currency to record an amount.",
    dimensionPositive: "Dimensions must be greater than zero.",
    yearStartRequired: "Enter the year.",
    yearEndRequired: "Enter the end of the range.",
    yearOrder: "The end of a range cannot come before its start.",
    subtypeMismatch: "This subtype does not belong to the chosen type.",
    rightsNoteRequired: "Custom terms need a rights note.",
    partsEmpty: "For a multi-part work, record each panel separately.",
    partLine: (n: number) => `Part ${n}`,
    partLabelPlaceholder: "e.g. left panel",
    addPart: "Add part",
    editionBoth: "Enter edition number and size together, or leave both empty.",
    editionMin: "Edition values must be at least 1.",
    editionOrder: "Edition number cannot exceed edition size.",
  },

  share: {
    title: "Public page",
    toggle: "Show this work via QR code",
    intro:
      "A visitor who scans the code sees title, image, dimensions, medium, and year. Notes, prices, provenance, documents, and inventory codes stay private.",
    offNote: "Off — scanning the code shows nothing.",
    printHint: "Print this code on the work’s label.",
    copy: "Copy",
    copied: "Link copied",
    copyFailed: "Could not copy — select the link and copy manually.",
    send: "Send",
    open: "Open",
    rotate: "New code",
    rotateTitle: "Generate a new code",
    rotatePrompt: "The current code will stop working; labels already printed with it will not.",
    rotated: "New code generated",
    published: "Public page is on",
    unpublished: "Public page is off",
    qrLabel: "Public page QR code",
    download: "Get QR file",
    downloading: "Sending…",
    downloadHint: "A print-ready QR file is sent in your chat with the bot.",
    sent: "QR file sent in your chat with the bot",
    startBot: "Open the bot chat and press Start, then try again.",
    sendFailed: "Could not send — Telegram was unreachable. Try again.",
  },

  catalog: {
    settingsTitle: "Public page & co-editors",
    switchLabel: "Choose catalog",
    ownCatalog: "My archive",
    sharedCatalog: "Shared archive",
    publicCatalogTitle: "Public catalog",
    catalogPublicToggle: "Show my public catalog at this slug",
    profileSaved: "Settings saved",
    membersTitle: "Co-editors",
    noMembers: "No one invited yet.",
    anonymousMember: "Unnamed",
    memberRemoved: "Co-editor removed",
    removeCoeditTitle: "Remove co-editor?",
    removeCoeditBody: "They will no longer see this archive. Their own archive stays intact.",
    removeCoeditConfirm: "Remove access",
    yourTelegramId: "Your Telegram id (for TELEGRAM_ALLOWED_USER_IDS)",
    inviteTitle: "Invite to co-edit this archive",
    inviteHint:
      "They work on your archive — not a separate catalog. Guest opens the link, Start, then Open App.",
    inviteCreate: "Create co-edit link",
    inviteCreating: "Creating…",
    inviteCreated: "Co-edit link created",
    inviteLinkLabel: "Co-edit link",
    inviteShareText: "Invitation to co-edit an art archive",
    theme: {
      default: "Default",
      tina: "Tina",
    },
  },

  access: {
    title: "App access",
    hint: "Invite with links only. Each person gets their own archive; co-editing yours is separate.",
    noMembers: "No one on the access list yet.",
    anonymousMember: "Unnamed",
    roleAdmin: "Admin",
    roleUser: "User",
    memberRemoved: "App access removed",
    removeTitle: "Remove app access?",
    removeBody:
      "They will no longer be able to open the Mini App. Their archive and artworks are kept; only admission is revoked.",
    removeConfirm: "Remove access",
    admitTitle: "Invite a user",
    admitHint: "Opens the app with their own archive. Cannot manage access.",
    admitInviteCreate: "Create user link",
    admitInviteCreated: "User link created",
    admitLinkLabel: "User invite link",
    adminTitle: "Invite an admin",
    adminHint: "Opens the app with their own archive, plus can invite users (and admins).",
    adminInviteCreate: "Create admin link",
    adminInviteCreated: "Admin link created",
    adminLinkLabel: "Admin invite link",
    admitShareText: "User invitation to Art Directory",
    adminShareText: "Admin invitation to Art Directory",
  },

  publicPage: {
    notFound: "This work is not available",
    notFoundBody: "The link expired or the owner turned public display off.",
    catalogNotFound: "This catalog is not available",
    catalogNotFoundBody: "The slug is wrong or the public catalog is off.",
    retry: "Try again",
    gallery: (n: number, total: number) => `Image ${n} of ${total}`,
    specs: "Details",
    edition: (n: number, size: number) => `Edition ${n} of ${size}`,
    lifeSize: "Life-size",
    uncategorized: "Uncategorized",
    filterAll: "All",
    filterCategory: "Category",
    filterArtist: "Artist",
    searchPlaceholder: "Search the catalog…",
    noFilterHits: "Nothing matches these filters",
    clearFilters: "Clear filters",
    navWorks: "Works",
    navWork: "Work",
    navBack: "Back to catalog",
    footer: "Public archive catalog",
  },

  detail: {
    untitled: "Untitled",
    unknownArtist: "Artist unknown",
    noMedia: "No image",
    deletePrompt: "This work leaves the list. You can restore it right away.",
    deleted: "Work deleted",
    restored: "Work restored",
    authenticityNote:
      "These are recorded documents, not a confirmation of authenticity. The final judgement rests with an expert.",
    noFiles: "No files attached",
    accessNote: "This work is shared with no one; the access level is only recorded.",
  },

  media: {
    primary: "Primary image",
    conditionFiles: "Condition documents",
    authenticityFiles: "Evidence / certificates",
    pickImageOrVideo: "Add an image or video",
    pickFile: "Add a file",
    uploading: "Uploading…",
    processing: "Preparing…",
    uploadFailed: "Upload failed",
    captionPlaceholder: "Caption (optional)",
    tooLarge: (limitMb: number) => `This file is larger than ${limitMb} MB.`,
    tooMany: (max: number) => `A work holds at most ${max} images or videos.`,
    quotaExceeded: "Your storage is full.",
    wrongKind: "This kind of file is not allowed here.",
    afterSave: "Uploads once the work is saved.",
    rightsLicense: enLicenses,
    accessLevel: enAccess,
    rightsNotePlaceholder: "The terms covering this image",
    accessNote:
      "This archive is yours alone. The access level is recorded now, for a later sharing phase.",
  },

  errors: {
    title: "Something went wrong",
    network: "Could not reach the server.",
    notInTelegram: "Open this page from inside Telegram.",
    unauthorized: "You could not be verified. Reopen the app from inside Telegram.",
    forbidden: "This Telegram account does not have access.",
    notFound: "This work could not be found.",
    throttled: (seconds: number) => `Too many requests. Wait ${seconds} seconds.`,
    referenced: "This option is still used by some works, so it cannot be removed.",
    server: "Server error.",
    artistRequired: "An artist name cannot be blank.",
    amountNegative: "An amount cannot be negative.",
    unknownYearWithValue: "An unknown year cannot carry a number.",
    typeInUse: "This type is used by some works — deactivate it instead of deleting it.",
    duplicateOption: "This option already exists.",
  },

  loading: "Loading…",
};

export const strings: Record<Lang, Strings> = { fa, en };

/** For components. Re-renders the caller when the language changes. */
export function useT(): Strings {
  return strings[useLang()];
}

/** For the handful of callers that are not components (api/*, form helpers). */
export function tr(lang: Lang = getLang()): Strings {
  return strings[lang];
}

/**
 * DRF speaks English. These are the messages the server can realistically send
 * to this UI; anything unmapped falls through as-is rather than being hidden.
 * Mapping to a *selector* rather than a string is what keeps this bilingual.
 */
const serverMessages: Array<[RegExp, (s: Strings) => string]> = [
  [/^Title cannot be blank\.$/, (s) => s.form.requiredTitle],
  [/^Artist name cannot be blank\.$/, (s) => s.errors.artistRequired],
  [/does not belong to/, (s) => s.form.subtypeMismatch],
  [/^A currency is required when any amount is filled in\.$/, (s) => s.form.currencyRequired],
  [/^Amounts cannot be negative\.$/, (s) => s.errors.amountNegative],
  [/^Dimensions must be greater than 0\.$/, (s) => s.form.dimensionPositive],
  [/^An unknown year cannot carry/, (s) => s.errors.unknownYearWithValue],
  [/^year_start is required/, (s) => s.form.yearStartRequired],
  [/^year_end is required for a year range\.$/, (s) => s.form.yearEndRequired],
  [/^year_end cannot be before year_start\.$/, (s) => s.form.yearOrder],
  [/^Storage quota exceeded\.$/, (s) => s.media.quotaExceeded],
  [/^Deactivate this type instead/, (s) => s.errors.typeInUse],
  [/already exists|must make a unique set/i, (s) => s.errors.duplicateOption],
  [/^This record is still referenced/, (s) => s.errors.referenced],
  [/^Custom rights need a note/, (s) => s.form.rightsNoteRequired],
];

export function localizeServerMessage(message: string, lang: Lang = getLang()): string {
  const s = strings[lang];
  for (const [pattern, select] of serverMessages) {
    if (pattern.test(message)) return select(s);
  }
  return message;
}
